import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { take } from 'rxjs';
import { WelcomeWizardService } from '@app/core/services/welcome-wizard.service';
import { SettingService } from '@app/core/services/models/setting.service';
import { Setting } from '@app/core/models/setting.models';
import { BaseUrlService } from '@app/core/services/base-url.service';
import { CompressService } from '@app/core/services/compress.service';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { InputNumberModule } from 'primeng/inputnumber';
import { CardModule } from 'primeng/card';
import { LoaderService } from '@app/core/services/loader.service';
import { WIZARD_CONFIG, getAllWizardFieldNames, WizardStep, WizardGroup, WizardField } from './wizard.config';
import { INSTALLATION_SETTING_NAME } from '@app/core/services/welcome-wizard.service';
import { ConsoleService } from '@app/core/services/console.service';

@Component({
  selector: 'app-welcome-wizard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ButtonModule,
    InputTextModule,
    InputTextarea,
    InputNumberModule,
    CardModule
  ],
  templateUrl: './welcome-wizard.component.html',
  styleUrl: './welcome-wizard.component.scss'
})
export class WelcomeWizardComponent implements OnInit {
  private welcomeService = inject(WelcomeWizardService);
  private settingService = inject(SettingService);
  private baseUrl = inject(BaseUrlService);
  private compressService = inject(CompressService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private loader = inject(LoaderService);
  private cdr = inject(ChangeDetectorRef);
  private _consoleService = inject(ConsoleService);
  readonly wizardConfig = WIZARD_CONFIG.wizard;

  /** 0 = intro, 1 à N = sous-étapes (step+group) */
  activeIndex = 0;
  /** true tant que la vérification installation === '1' est en cours */
  checkingInstallation = true;
  saving = false;
  imageCompressing = false;
  /** Affiché après enregistrement réussi : choix de la prochaine action */
  configurationSaved = false;

  settings: Map<string, Setting> = new Map();
  formValues: Record<string, string | number> = {};

  /** Fichiers image en attente (field name → File) */
  private pendingImageFiles = new Map<string, File>();
  /** Aperçus (field name → object URL ou valeur existante) */
  private pendingImagePreviews = new Map<string, string>();

  /** Sous-étapes aplaties : chaque (step, group) = une sous-étape */
  private _allSubSteps: { stepLabel: string; groupLabel: string; step: WizardStep; group: WizardGroup }[] = [];

  get allSubSteps() {
    if (this._allSubSteps.length) return this._allSubSteps;
    const flat: typeof this._allSubSteps = [];
    for (const step of this.wizardConfig.steps) {
      for (const group of step.groups) {
        flat.push({ stepLabel: step.label, groupLabel: group.label, step, group });
      }
    }
    this._allSubSteps = flat;
    return flat;
  }

  get totalSteps(): number {
    return this.allSubSteps.length;
  }

  get currentSubStep(): (typeof this._allSubSteps)[0] | null {
    if (this.activeIndex < 1 || this.activeIndex > this.totalSteps) return null;
    return this.allSubSteps[this.activeIndex - 1];
  }

  /** Stepper plat : une entrée par groupe (Identité administrative, Coordonnées, etc.) */
  get stepperItems(): { stepNumber: number; label: string; flatIndex: number }[] {
    let flatIndex = 1;
    const items: { stepNumber: number; label: string; flatIndex: number }[] = [];
    for (const step of this.wizardConfig.steps) {
      for (const group of step.groups) {
        items.push({ stepNumber: items.length + 1, label: group.label, flatIndex });
        flatIndex++;
      }
    }
    return items;
  }

  isStepperCompleted(flatIndex: number): boolean {
    return this.activeIndex > flatIndex;
  }

  isStepperCurrent(flatIndex: number): boolean {
    return this.activeIndex === flatIndex;
  }

  isStepperUpcoming(flatIndex: number): boolean {
    return this.activeIndex < flatIndex;
  }

  onImageFileChange(event: Event, field: WizardField): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    if (!file.type.startsWith('image/')) {
      this.messageService.add({ severity: 'warn', summary: 'Veuillez sélectionner une image.' });
      return;
    }
    this.imageCompressing = true;
    this.compressService.compress(file).pipe(take(1)).subscribe({
      next: (compressed) => {
        this.imageCompressing = false;
        const oldPreview = this.pendingImagePreviews.get(field.name);
        if (oldPreview && oldPreview.startsWith('blob:')) URL.revokeObjectURL(oldPreview);
        this.pendingImageFiles.set(field.name, compressed);
        this.pendingImagePreviews.set(field.name, URL.createObjectURL(compressed));
        this.cdr.markForCheck();
      },
      error: () => {
        this.imageCompressing = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur lors de la compression' });
      }
    });
    input.value = '';
  }

  getImagePreview(fieldName: string): string {
    const preview = this.pendingImagePreviews.get(fieldName);
    if (preview) return preview;
    const value = this.formValues[fieldName];
    if (typeof value === 'string' && value) {
      if (value.startsWith('data:') || value.startsWith('http')) return value;
      return this.baseUrl.uploadPath + (value.startsWith('/') ? value : '/' + value);
    }
    return '';
  }

  ngOnInit(): void {
    this.loader.stopLoading();
    this._consoleService.logVar('ngOnInit', null);
    const fieldNames = getAllWizardFieldNames();
    this.loader.startLoading();
    this.settingService.all({ skipLoader: true }).subscribe({
      next: (res: any) => {
        this.loader.stopLoading();
        this.checkingInstallation = false;
        const list = Array.isArray(res) ? res : (res?.data ?? []);
        const installation = list.find((s: any) => s.name === INSTALLATION_SETTING_NAME);
        if (installation?.value !== '1' && installation?.value !== 1) {
          this.router.navigate(['/moni', 'dashboard']);
          return;
        }
        fieldNames.forEach(name => {
          const s = list.find((x: any) => x.name === name);
          if (s) {
            this.settings.set(name, s);
            const val = s.value ?? '';
            this.formValues[name] = this.isNumberField(name) ? (parseFloat(val) || 0) : val;
          } else {
            this.formValues[name] = this.isNumberField(name) ? 0 : '';
          }
        });
      },
      error: () => {
        this.loader.stopLoading();
        this.checkingInstallation = false;
        this.router.navigate(['/moni', 'dashboard']);
      }
    });
  }

  private isNumberField(name: string): boolean {
    for (const step of this.wizardConfig.steps) {
      for (const group of step.groups) {
        const field = group.fields.find(f => f.name === name);
        if (field?.type === 'number') return true;
      }
    }
    return false;
  }

  startWizard(): void {
    this.activeIndex = 1;
  }

  /**
   * Vérifie que tous les champs obligatoires de l'étape courante sont remplis.
   */
  isCurrentStepValid(): boolean {
    const current = this.currentSubStep;
    if (!current) return true;
    for (const field of current.group.fields) {
      if (!field.required) continue;
      if (field.type === 'image') {
        const hasImage =
          this.pendingImageFiles.has(field.name) ||
          (this.formValues[field.name] != null && String(this.formValues[field.name]).trim() !== '');
        if (!hasImage) return false;
      } else if (field.type === 'number') {
        const v = this.formValues[field.name];
        if (v === undefined || v === null || v === '') return false;
        if (typeof v !== 'number' && (typeof v !== 'string' || isNaN(Number(v)))) return false;
      } else {
        const v = this.formValues[field.name];
        const s = (v != null ? String(v) : '').trim();
        if (s === '') return false;
      }
    }
    return true;
  }

  next(): void {
    if (!this.isCurrentStepValid()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Champs obligatoires',
        detail: 'Veuillez remplir tous les champs marqués d’un astérisque avant de continuer.'
      });
      return;
    }
    // En quittant « Coordonnées », mettre à jour les pieds de page (interface uniquement, enregistrement à la fin)
    const current = this.currentSubStep;
    if (current?.group.key === 'company_contact') {
      const footerComputed = this.buildFooterFromCompanySettings();
      this.formValues['quote_footer'] = footerComputed;
      this.formValues['invoice_footer'] = footerComputed;
    }
    this.activeIndex = Math.min(this.activeIndex + 1, this.totalSteps);
  }

  prev(): void {
    this.activeIndex = Math.max(this.activeIndex - 1, 0);
  }

  /**
   * Construit le pied de page devis/facture à partir des settings entreprise
   * (identité administrative + coordonnées).
   */
  private buildFooterFromCompanySettings(): string {
    const name = String(this.formValues['company_name'] ?? '').trim();
    const rc = String(this.formValues['company_rc_number'] ?? '').trim();
    const tahiti = String(this.formValues['company_tahiti_number'] ?? '').trim();
    const address = String(this.formValues['company_address'] ?? '').trim();
    const zipcode = String(this.formValues['company_zipcode'] ?? '').trim();
    const city = String(this.formValues['company_city'] ?? '').trim();
    const country = String(this.formValues['company_country'] ?? '').trim();
    const parts1 = [
      rc ? `RC : ${rc}` : '',
      tahiti ? `N° Tahiti : ${tahiti}` : '',
      'BANQUE : –'
    ].filter(Boolean);
    const line1 = parts1.length ? parts1.join(' | ') : 'RC : – | N° Tahiti : – | BANQUE : –';
    const addressLine = [address, zipcode, city].filter(Boolean).join(' ');
    const line2Parts = [name, addressLine, country].filter(Boolean);
    const line2 = line2Parts.length ? line2Parts.join(' – ') : '–';
    return `${line1}<br/>${line2}`;
  }

  saveAndFinish(): void {
    if (!this.isCurrentStepValid()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Champs obligatoires',
        detail: 'Veuillez remplir tous les champs marqués d’un astérisque avant de terminer.'
      });
      return;
    }
    this.saving = true;
    const formData = new FormData();

    // Marquer l'installation comme terminée (backend le gère)
    formData.append('installation', '0');

    // Ajouter tous les champs texte/number (quote_footer / invoice_footer déjà mis à jour au « Suivant » après Coordonnées)
    for (const [name, value] of Object.entries(this.formValues)) {
      if (value !== undefined && value !== null) {
        formData.append(name, String(value));
      }
    }

    // Ajouter les fichiers image
    for (const [fieldName, file] of this.pendingImageFiles) {
      formData.append(fieldName, file, file.name);
    }

    this.settingService.edit('init', formData, { skipLoader: true }).subscribe({
      next: () => {
        this.saving = false;
        this.welcomeService.finishFromStoreInit();
        this.messageService.add({ severity: 'success', summary: 'Configuration enregistrée' });
        this.configurationSaved = true;
      },
      error: () => {
        this.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur lors de l\'enregistrement' });
      }
    });
  }
}
